"use client";

import { useEffect, useMemo } from "react";
import { resolveLiteralBoolean, resolvePath, resolveStringArray, resolveText, resolveUnknown, useSeedHasSelection } from "./binding";
import type { MacaronRenderContext, RecordAny } from "./types";

export function MacaronDropdownSelection({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const items = useMemo(() => resolveStringArray(config.items, ctx.data), [config.items, ctx.data]);
  const rawSelection = useMemo(() => {
    const value = resolveUnknown(config.selection, ctx.data);
    return Array.isArray(value) ? value : [];
  }, [config.selection, ctx.data]);
  const value = useMemo(() => rawSelection.find((entry): entry is string => typeof entry === "string" && items.includes(entry)) || "", [items, rawSelection]);
  const placeholder = resolveText(config.placeholder, ctx.data) || "Select location";
  const selectionPath = resolvePath(config.selection);
  const hasSelectionPath = resolvePath(config.hasSelection);
  const hasSelectionLiteral = resolveLiteralBoolean(config.hasSelection);
  const updateData = ctx.updateData;

  useSeedHasSelection(hasSelectionPath, hasSelectionLiteral, ctx.data, updateData);
  useEffect(() => {
    if (!hasSelectionPath) return;
    updateData?.(hasSelectionPath, Boolean(value));
  }, [hasSelectionPath, updateData, value]);

  return (
    <select
      className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm"
      value={value}
      disabled={!selectionPath || items.length === 0}
      onChange={(e) => {
        if (!selectionPath) return;
        const nextValue = e.target.value;
        ctx.updateData?.(selectionPath, nextValue ? [nextValue] : []);
        if (hasSelectionPath) ctx.updateData?.(hasSelectionPath, Boolean(nextValue));
      }}
    >
      <option value="">{placeholder}</option>
      {items.map((item, i) => <option key={`${item}-${i}`} value={item}>{item}</option>)}
    </select>
  );
}
